import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Ruler } from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import styles from './UnitDrawer.module.css';

const UnitDrawer = ({ isOpen, onClose, onSave, unit }) => {
  const [formData, setFormData] = useState({
    unit_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (unit && unit.id) {
        fetchUnitDetails(unit.id);
      } else {
        setFormData({
          unit_name: ''
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, isOpen]);

  const fetchUnitDetails = async (id) => {
    setIsFetching(true);
    try {
      const response = await api.get(`/units/${id}`);
      if (response.data.success) {
        setFormData({
          unit_name: response.data.data.unit_name || ''
        });
      }
    } catch (error) {
      console.error('Error fetching unit details:', error);
      toast.error('Failed to load unit details');
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.unit_name.trim()) {
      toast.error('Unit name is required');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (unit) {
        response = await api.put(`/units/${unit.id}`, formData);
      } else {
        response = await api.post('/units', formData);
      }

      if (response.data.success) {
        toast.success(unit ? 'Unit updated successfully' : 'Unit created successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving unit:', error);
      toast.error(error.response?.data?.message || 'Failed to save unit');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{unit ? 'Edit Unit' : 'Add Unit'}</h2>
            <p>{unit ? 'Update details for this measurement unit.' : 'Create a new unit for materials and projects.'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          {isFetching ? (
            <div className={styles['fetching-state']}>
              <Loader2 size={40} className={styles['spinner']} />
              <p>Fetching technical details...</p>
            </div>
          ) : (
            <>
              <div className={styles['form-scroll-area']}>
                <div className={styles['form-group']}>
                  <label htmlFor="unit_name">
                    <Ruler size={16} />
                    <span>Unit Name <span className={styles['required-star']}>*</span></span>
                  </label>
                  <input
                    type="text"
                    id="unit_name"
                    name="unit_name"
                    placeholder="e.g. Kilograms, Meters, Pieces"
                    value={formData.unit_name}
                    onChange={handleChange}
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div className={styles['drawer-footer']}>
                <button type="button" className={styles['cancel-btn']} onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className={styles['save-btn']} disabled={loading}>
                  {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
                  <span>{unit ? 'Update Unit' : 'Save Unit'}</span>
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default UnitDrawer;
